import React, { useMemo } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuItemOption,
  MenuList,
  MenuOptionGroup,
  Select,
  Spinner,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { DASHBOARD_PERSON_TYPES } from "./dashboardUtils";

const FieldLabel = ({ children }) => (
  <Text
    fontSize="11px"
    fontWeight="800"
    textTransform="uppercase"
    letterSpacing="0.04em"
    color="portal.muted"
    sx={{ marginBottom: "4px !important" }}
  >
    {children}
  </Text>
);

const DashboardFilters = ({
  t,
  isLoading,
  businessUnits,
  selectedBusinessUnitIds,
  allBusinessUnitIds,
  singleBusinessUnitFallbackId,
  onBusinessUnitChange,
  canUseMultiBusinessUnitFilter,
  personOptions,
  selectedPersonValue,
  onPersonChange,
  canUsePersonFilter,
  chartGranularity,
  onGranularityChange,
}) => {
  const selectedBusinessUnits = useMemo(
    () =>
      (businessUnits || []).filter((businessUnit) =>
        selectedBusinessUnitIds.includes(String(businessUnit.id))
      ),
    [businessUnits, selectedBusinessUnitIds]
  );

  const isAllSelected =
    Boolean(allBusinessUnitIds.length) &&
    selectedBusinessUnitIds.length === allBusinessUnitIds.length;

  const selectedBusinessUnitLabel = useMemo(() => {
    if (isAllSelected) return t("dashboard.filters.businessUnits.all");
    if (!selectedBusinessUnits.length) return t("dashboard.filters.businessUnits.none");
    if (selectedBusinessUnits.length === 1) {
      return (
        selectedBusinessUnits[0]?.unit_name ||
        selectedBusinessUnits[0]?.unit_code ||
        t("dashboard.filters.businessUnits.singleFallback")
      );
    }
    return t("dashboard.filters.businessUnits.count", {
      count: selectedBusinessUnits.length,
    });
  }, [isAllSelected, selectedBusinessUnits, t]);

  return (
    <Flex align="flex-end" gap="8px" flexWrap="wrap" mb="12px">
      {canUseMultiBusinessUnitFilter && (
        <Box maxW={{ base: "full", md: "280px" }} minW="180px">
          <FieldLabel>{t("dashboard.filters.businessUnits.label")}</FieldLabel>
          <Menu closeOnSelect={false}>
            <MenuButton
              as={Button}
              variant="secondary"
              h="31px"
              fontSize="12px"
              fontWeight="600"
              rightIcon={<ChevronDownIcon />}
              w="full"
              textAlign="left"
            >
              <Text as="span" isTruncated sx={{ marginBottom: "0 !important" }}>
                {selectedBusinessUnitLabel}
              </Text>
            </MenuButton>
            <MenuList>
              <MenuItem
                onClick={() =>
                  onBusinessUnitChange(
                    isAllSelected
                      ? [singleBusinessUnitFallbackId || allBusinessUnitIds[0]].filter(Boolean)
                      : allBusinessUnitIds
                  )
                }
              >
                {isAllSelected
                  ? t("dashboard.filters.businessUnits.showSingle")
                  : t("dashboard.filters.businessUnits.selectAll")}
              </MenuItem>
              <MenuOptionGroup
                type="checkbox"
                title={t("dashboard.filters.businessUnits.menuTitle")}
                value={selectedBusinessUnitIds}
                onChange={(values) => onBusinessUnitChange(values.map(String))}
              >
                {(businessUnits || []).map((businessUnit) => (
                  <MenuItemOption key={businessUnit.id} value={String(businessUnit.id)}>
                    {businessUnit.unit_name || businessUnit.unit_code}
                  </MenuItemOption>
                ))}
              </MenuOptionGroup>
            </MenuList>
          </Menu>
        </Box>
      )}

      {canUsePersonFilter && (
        <Box maxW={{ base: "full", md: "300px" }} minW="180px">
          <FieldLabel>{t("dashboard.filters.person.label")}</FieldLabel>
          <Select
            h="31px"
            fontSize="12px"
            fontWeight="600"
            value={selectedPersonValue}
            onChange={(event) => onPersonChange(event.target.value)}
            aria-label={t("dashboard.filters.person.label")}
          >
            <option value={DASHBOARD_PERSON_TYPES.ALL}>
              {t("dashboard.filters.person.all")}
            </option>
            {personOptions.salesOptions.length > 0 && (
              <optgroup label={t("dashboard.filters.person.groups.sales")}>
                {personOptions.salesOptions.map((option) => (
                  <option key={`sales-${option.id}`} value={`${DASHBOARD_PERSON_TYPES.SALES}:${option.id}`}>
                    {option.name}
                  </option>
                ))}
              </optgroup>
            )}
            {personOptions.recruiterOptions.length > 0 && (
              <optgroup label={t("dashboard.filters.person.groups.recruiter")}>
                {personOptions.recruiterOptions.map((option) => (
                  <option
                    key={`recruiter-${option.id}`}
                    value={`${DASHBOARD_PERSON_TYPES.RECRUITER}:${option.id}`}
                  >
                    {option.name}
                  </option>
                ))}
              </optgroup>
            )}
          </Select>
        </Box>
      )}

      <Box>
        <FieldLabel>{t("dashboard.filters.granularity.label", "Granularity")}</FieldLabel>
        <ButtonGroup size="sm" isAttached variant="outline">
          <Button
            h="31px"
            fontSize="12px"
            variant={chartGranularity === "month" ? "primary" : "secondary"}
            onClick={() => onGranularityChange?.("month")}
          >
            {t("dashboard.pilotChart.granularity.month", "Month")}
          </Button>
          <Button
            h="31px"
            fontSize="12px"
            variant={chartGranularity === "week" ? "primary" : "secondary"}
            onClick={() => onGranularityChange?.("week")}
          >
            {t("dashboard.pilotChart.granularity.week", "Week")}
          </Button>
        </ButtonGroup>
      </Box>

      {isLoading && <Spinner size="sm" color="portal.primary" mb="6px" />}
    </Flex>
  );
};

export default DashboardFilters;
